/**
 * Booking Status Utilities
 *
 * Utilities for determining booking status, color coding, and absence calculations.
 * Used for visual indicators in BookingCard and Schedule components.
 */

import type { BookingStatus } from "@/components/booking/display/BookingCard/types";

/**
 * Determine booking status based on hours
 *
 * Priority order (matches Vue behavior):
 * 1. Overbooked (if totalHours > dayHours) - always takes precedence
 * 2. Fully Booked (if totalHours >= dayHours - 0.5) - includes full absence days
 * 3. Booked (if totalHours > 0)
 * 4. Free (if totalHours == 0)
 *
 * Note: Uses 0.5-hour padding for fullyBooked to match Vue behavior.
 * For an 8-hour day, 7.5+ hours is considered fully booked.
 *
 * IMPORTANT: A day with 8h absence is "fullyBooked" (green border), not "absence".
 * The absence fill overlay provides the cyan background. This matches Vue where
 * both .fullyBooked (green border) and .fullFravær (cyan bg) classes are applied.
 *
 * @param projectHours - Hours booked on projects (excluding absence)
 * @param absenceHours - Hours marked as absence
 * @param dayHours - Standard hours in a working day (typically 8)
 * @returns BookingStatus enum value
 *
 * @example
 * ```ts
 * getBookingStatus(8, 0, 8);   // "fullyBooked"
 * getBookingStatus(7.5, 0, 8); // "fullyBooked" (within 0.5hr padding)
 * getBookingStatus(4, 0, 8);   // "booked"
 * getBookingStatus(0, 8, 8);   // "fullyBooked" (full absence = green border + cyan fill)
 * getBookingStatus(10, 0, 8);  // "overbooked"
 * getBookingStatus(8, 8, 8);   // "overbooked" (16h total > 8h day)
 * getBookingStatus(0, 0, 8);   // "free"
 * ```
 */
export function getBookingStatus(
  projectHours: number,
  absenceHours: number,
  dayHours: number,
): BookingStatus {
  // Defensive: handle invalid inputs
  if (dayHours <= 0 || projectHours < 0 || absenceHours < 0) {
    return "free";
  }

  const totalHours = projectHours + absenceHours;

  // Priority 1: Overbooked always takes precedence (matches Vue)
  if (totalHours > dayHours) {
    return "overbooked";
  }

  // Priority 2: Fully booked (with 0.5-hour padding to match Vue)
  // This includes days that are fully booked with absence - they get green border,
  // and the absence fill overlay provides the cyan background.
  if (totalHours >= dayHours - 0.5) {
    return "fullyBooked";
  }

  // Priority 3: Full absence (only pure absence days that aren't fully booked)
  // This case is rare - would need absenceHours >= dayHours but totalHours < dayHours - 0.5
  // which is impossible. Kept for defensive coding.
  if (absenceHours >= dayHours) {
    return "absence";
  }

  // Priority 4: Partially booked
  if (totalHours > 0) {
    return "booked";
  }

  // Priority 5: No bookings
  return "free";
}

/**
 * Get color class name for booking status
 *
 * Maps booking status to color strings used in UI components.
 * Colors align with Brilliant design system.
 *
 * @param status - BookingStatus value
 * @returns Color class name
 *
 * @example
 * ```ts
 * getStatusColorClass("free");        // "grey"
 * getStatusColorClass("booked");      // "orange"
 * getStatusColorClass("fullyBooked"); // "green"
 * getStatusColorClass("overbooked");  // "red"
 * getStatusColorClass("absence");     // "cyan"
 * ```
 */
export function getStatusColorClass(
  status: BookingStatus,
): "grey" | "orange" | "green" | "red" | "cyan" {
  switch (status) {
    case "free":
      return "grey";
    case "booked":
      return "orange";
    case "fullyBooked":
      return "green";
    case "overbooked":
      return "red";
    case "absence":
      return "cyan";
    default:
      return "grey";
  }
}

/**
 * Calculate absence fill ratio for gradient indicators
 *
 * Returns a value between 0 and 1 representing the percentage of
 * the day filled by absence hours. Capped at 1.0 (100%).
 *
 * Used for visual gradient fills in BookingCard component.
 *
 * @param absenceHours - Hours marked as absence
 * @param dayHours - Standard hours in a working day (typically 8)
 * @returns Fill ratio from 0 to 1, or 0 for invalid inputs
 *
 * @example
 * ```ts
 * calculateAbsenceFill(0, 8);  // 0 (no absence)
 * calculateAbsenceFill(4, 8);  // 0.5 (half day)
 * calculateAbsenceFill(8, 8);  // 1 (full day)
 * calculateAbsenceFill(12, 8); // 1 (capped at 100%)
 * ```
 */
export function calculateAbsenceFill(
  absenceHours: number,
  dayHours: number,
): number {
  // Defensive: handle invalid inputs
  if (dayHours <= 0 || absenceHours <= 0 || !isFinite(absenceHours)) {
    return 0;
  }

  const ratio = absenceHours / dayHours;

  // Ensure ratio is valid and cap at 1.0
  if (!isFinite(ratio) || ratio <= 0) {
    return 0;
  }

  return Math.min(1, ratio);
}

/**
 * Calculate absence ratio without capping
 *
 * Similar to calculateAbsenceFill but allows values > 1.0 for
 * cases where absence hours exceed day capacity.
 *
 * @param absenceHours - Hours marked as absence
 * @param dayHours - Standard hours in a working day
 * @returns Absence ratio (can be > 1), or 0 for invalid inputs
 *
 * @example
 * ```ts
 * calculateAbsenceRatio(4, 8);  // 0.5
 * calculateAbsenceRatio(8, 8);  // 1.0
 * calculateAbsenceRatio(12, 8); // 1.5 (not capped)
 * ```
 */
export function calculateAbsenceRatio(
  absenceHours: number,
  dayHours: number,
): number {
  if (dayHours <= 0 || absenceHours <= 0) {
    return 0;
  }
  return absenceHours / dayHours;
}

/**
 * Check if booking hours exceed day capacity
 *
 * @param totalHours - Total booked hours (project + absence)
 * @param dayHours - Standard hours in a working day
 * @returns True if overbooked
 *
 * @example
 * ```ts
 * isOverbooked(9, 8);   // true
 * isOverbooked(8, 8);   // false
 * isOverbooked(7.5, 8); // false
 * ```
 */
export function isOverbooked(totalHours: number, dayHours: number): boolean {
  if (dayHours <= 0) return false;
  return totalHours > dayHours;
}

/**
 * Check if booking hours meet fully booked threshold
 *
 * Uses 0.5-hour padding to match Vue behavior.
 * For an 8-hour day, 7.5+ hours is considered fully booked.
 *
 * @param totalHours - Total booked hours (project + absence)
 * @param dayHours - Standard hours in a working day
 * @returns True if fully booked (within 0.5hr of day capacity)
 *
 * @example
 * ```ts
 * isFullyBooked(8, 8);   // true
 * isFullyBooked(7.5, 8); // true (within 0.5hr padding)
 * isFullyBooked(7.4, 8); // false
 * isFullyBooked(8.1, 8); // false (overbooked, not fully booked)
 * ```
 */
export function isFullyBooked(totalHours: number, dayHours: number): boolean {
  if (dayHours <= 0) return false;
  return totalHours >= dayHours - 0.5 && totalHours <= dayHours;
}

/**
 * Check if there are any absence hours
 *
 * @param absenceHours - Hours marked as absence
 * @param dayHours - Standard hours in a working day (for API consistency with other functions)
 * @returns True if any absence hours exist
 *
 * @example
 * ```ts
 * hasAbsence(0, 8);   // false
 * hasAbsence(0.5, 8); // true
 * hasAbsence(8, 8);   // true
 * ```
 */
export function hasAbsence(
  absenceHours: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _dayHours: number,
): boolean {
  return absenceHours > 0;
}
